import { useMemo } from 'react';
import type {
	RouterContext,
	RouteResource,
	RouteResourceError,
	RouteResourceTimestamp,
} from '../../../common/types.tsx';

import type { RouteResourceUpdater } from '../../common/types.tsx';
import { useResourceBase } from './index.tsx';

type GenericOptions = {
	/**
	 * If true, errors will be thrown with the intent that they are caught by an error boundary (or the nearest suspense
	 * boundary in SSR).
	 * If false, `error` may be returned instead of data, making `data` nullable.
	 * Default: true.
	 */
	throwErrors: boolean;
};

export type SuspenseResource<RouteResourceData, Opts extends GenericOptions> = {
	key?: string;
	expiresAt: RouteResourceTimestamp;
	accessedAt: RouteResourceTimestamp;
	update: (getNewData: RouteResourceUpdater<RouteResourceData>) => void;
	refresh: () => void;
} & (Opts['throwErrors'] extends true
	? { data: RouteResourceData }
	: { data: RouteResourceData; error: null } | { data: null; error: RouteResourceError });

const useSuspenseResourceConfigurable = <RouteResourceData, const Opts extends GenericOptions>({
	resource,
	routerContext,
	throwErrors,
}: {
	resource: RouteResource<RouteResourceData>;
	routerContext?: RouterContext;
} & Opts): SuspenseResource<RouteResourceData, Opts> => {
	const state = useResourceBase(resource, {
		routerContext,
	});
	if (state.loading) {
		if (state.promise == null) {
			throw new Error(
				`Failed to throw promise for suspense in loading state of resource "${resource.type}" because promise is not defined`,
			);
		}
		throw state.promise;
	}
	if (state.error != null && throwErrors) {
		throw state.error;
	}
	// Throw if we are in the "default" state (resource wasn't defined on the route or was cleared)
	if (state.promise == null && state.error == null) {
		throw new Error(
			[
				`Tried to access resource "${resource.type}" which does not have any data.`,
				"This is likely because the resource is not declared in the routes's resources or was cleared.",
			].join('\n'),
		);
	}

	return useMemo(() => {
		const { data, error, key, expiresAt, accessedAt, update, refresh } = state;
		const result: Record<string, unknown> = {
			key,
			data,
			expiresAt,
			accessedAt,
			update,
			refresh,
		};
		if (!throwErrors) {
			result.error = error;
		}
		return result as SuspenseResource<RouteResourceData, Opts>;
	}, [state, throwErrors]);
};

/**
 * The preferred method to use for suspense resources:
 * - Loading triggers suspense by throwing a pending promise.
 * - Errors are thrown to error boundaries.
 * - Resources are expected to be defined in the route, not lazily loaded.
 */
export const useSuspenseResource = <RouteResourceData,>(
	resource: RouteResource<RouteResourceData>,
	{ routerContext }: { routerContext?: RouterContext } = {},
): SuspenseResource<RouteResourceData, { throwErrors: true }> =>
	useSuspenseResourceConfigurable<RouteResourceData, { throwErrors: true }>({
		resource,
		routerContext,
		throwErrors: true,
	});

/**
 * Accesses the route resource as described by {@link useSuspenseResource}, but returns `error` instead of throwing them.
 * @deprecated prefer using {@link useSuspenseResource} and defining error states at your error boundaries.
 */
export const useSuspenseResourceReturnErrors = <RouteResourceData,>(
	resource: RouteResource<RouteResourceData>,
	{ routerContext }: { routerContext?: RouterContext } = {},
): SuspenseResource<RouteResourceData, { throwErrors: false }> =>
	useSuspenseResourceConfigurable<RouteResourceData, { throwErrors: false }>({
		resource,
		routerContext,
		throwErrors: false,
	});
